import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, Download, FileText, CheckCircle2 } from "lucide-react";

const fmt = (a, c) => `${c === "INR" ? "₹" : "$"}${Number(a).toLocaleString()}`;

export default function OrderDetails() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const nav = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [reason, setReason] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await api.get(`/orders/${id}`); setOrder(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot load order"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Poll Stripe status if returning from checkout
  useEffect(() => {
    if (!sid) return;
    let n = 0; const t = setInterval(async () => {
      n++;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") { clearInterval(t); toast.success("Payment received."); load(); }
      } catch (e) { console.error("payment status poll:", e); }
      if (n > 8) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
  }, [sid, load]);

  const payStripe = async () => {
    const r = await api.post("/payments/checkout", { order_id: id, gateway: "stripe", origin_url: window.location.origin });
    window.location.href = r.data.url;
  };
  const payRazorpay = async () => {
    const r = await api.post("/payments/checkout", { order_id: id, gateway: "razorpay", origin_url: window.location.origin });
    if (r.data.demo_mode) {
      // Demo: confirm directly
      await api.post("/payments/razorpay/confirm", {
        razorpay_order_id: r.data.razorpay_order_id,
        razorpay_payment_id: `pay_demo_${Date.now()}`
      });
      toast.success("Demo payment captured");
      load(); return;
    }
    if (!window.Razorpay) { toast.error("Razorpay SDK not loaded"); return; }
    const opts = {
      key: r.data.key_id, amount: r.data.amount, currency: r.data.currency,
      order_id: r.data.razorpay_order_id, name: "Aloka",
      handler: async (resp) => {
        await api.post("/payments/razorpay/confirm", resp);
        toast.success("Payment captured"); load();
      }
    };
    new window.Razorpay(opts).open();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f); fd.append("kind", "kyc");
    try {
      await api.post(`/orders/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Uploaded"); load();
    } catch(e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const requestRefund = async () => {
    if (!reason) return toast.error("Reason required");
    await api.post("/refunds", { order_id: id, reason });
    toast.success("Refund request submitted for review"); setReason(""); load();
  };

  const deliver = async () => { await api.post(`/orders/${id}/deliver`); toast.success("Marked delivered"); load(); };
  const complete = async () => { await api.post(`/orders/${id}/complete`); toast.success("Order completed"); load(); };

  if (!order) return <div className="p-12 text-center text-slate-500">Loading…</div>;

  const isClient = user?.role === "client";
  const isPro = user?.role === "professional";
  const canPay = isClient && order.payment_status !== "paid" && order.status === "pending_payment";

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="order-page">
      <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold">Order</div>
      <h1 className="font-display text-3xl font-black text-slate-900">{order.service_title}</h1>
      <div className="text-sm text-slate-500 font-mono mt-1">{order.order_id}</div>

      <div className="grid md:grid-cols-3 gap-4 mt-8">
        <div className="border border-slate-200 rounded p-5 bg-white">
          <div className="text-xs uppercase tracking-widest text-slate-500">Amount</div>
          <div className="font-display text-3xl font-black text-slate-900">{fmt(order.amount, order.currency)}</div>
        </div>
        <div className="border border-slate-200 rounded p-5 bg-white">
          <div className="text-xs uppercase tracking-widest text-slate-500">Platform Fee (15%)</div>
          <div className="font-display text-3xl font-black text-slate-900">{fmt(order.platform_fee, order.currency)}</div>
        </div>
        <div className="border border-slate-200 rounded p-5 bg-white">
          <div className="text-xs uppercase tracking-widest text-slate-500">Pro Payout</div>
          <div className="font-display text-3xl font-black text-blue-600">{fmt(order.professional_payout, order.currency)}</div>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Badge variant="outline" className="text-xs">Status: {order.status}</Badge>
        <Badge variant="outline" className="text-xs">Payment: {order.payment_status || "unpaid"}</Badge>
      </div>

      {canPay && (
        <div className="mt-8 border border-slate-200 rounded p-6 bg-white" data-testid="pay-section">
          <h3 className="font-bold text-slate-900 mb-3">Make payment</h3>
          <p className="text-sm text-slate-600 mb-4">Pay securely. Funds are released to the professional only after you mark the work complete.</p>
          <div className="flex gap-3">
            <Button className="btn-primary" onClick={payStripe} data-testid="pay-stripe-btn">Pay with Stripe</Button>
            <Button className="btn-accent" onClick={payRazorpay} data-testid="pay-razorpay-btn">Pay with Razorpay</Button>
          </div>
        </div>
      )}

      {/* Documents */}
      <div className="mt-8 border border-slate-200 rounded p-6 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-900">Documents</h3>
          {(isClient || isPro) && (
            <>
              <input ref={fileRef} type="file" className="hidden" onChange={upload} data-testid="doc-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="doc-upload-btn">
                <Upload className="w-4 h-4 mr-1"/> Upload
              </Button>
            </>
          )}
        </div>
        <div className="space-y-2">
          {(order.documents || []).length === 0 && <div className="text-sm text-slate-500">No documents yet.</div>}
          {(order.documents || []).map(fid => (
            <a key={fid} href={`${API}/documents/${fid}`} target="_blank" rel="noreferrer"
               className="flex items-center gap-2 text-sm text-blue-700 hover:underline">
              <FileText className="w-4 h-4" /> {fid} <Download className="w-3 h-3"/>
            </a>
          ))}
        </div>
      </div>

      {/* Pro action */}
      {isPro && order.payment_status === "paid" && order.status === "in_progress" && (
        <div className="mt-6">
          <Button className="btn-primary" onClick={deliver} data-testid="mark-delivered-btn"><CheckCircle2 className="w-4 h-4 mr-1"/>Mark as delivered</Button>
        </div>
      )}

      {/* Client confirm / refund */}
      {isClient && order.status === "delivered" && (
        <div className="mt-6 border border-slate-200 rounded p-6 bg-white">
          <h3 className="font-bold text-slate-900 mb-3">How was the work?</h3>
          <div className="flex flex-wrap gap-3">
            <Button className="btn-primary" onClick={complete} data-testid="complete-order-btn">Approve & complete</Button>
          </div>
          <div className="mt-5">
            <div className="text-sm text-slate-600 mb-2">Not satisfied? Request a refund — Aloka will review quality.</div>
            <Textarea placeholder="Reason for refund…" value={reason} onChange={e=>setReason(e.target.value)} data-testid="refund-reason"/>
            <Button variant="outline" className="mt-2" onClick={requestRefund} data-testid="refund-btn">Request refund</Button>
          </div>
        </div>
      )}
    </div>
  );
}
